// 部屋の設定と統計を「人が読める文字列」にする。DOM に触らないので単体テストできる。
//
// ここで出す数値は試聴の記録に添える値なので、計算式は DOC-0021 §3 と揃える
// (研究用リポジトリの docs/search/0021-…。本リポジトリには無い)。

import {
  jitterDepthMs,
  packetMs,
  type AudioProfile,
  type Participant,
  type RoomInfo,
  type Topology,
} from "./control.ts";

/** 1 パケットあたりの付き物 (QUIC・UDP・IP)。本実装のヘッダ 12 B は測った bytes に入っている。 */
export const OVERHEAD_PER_PACKET = 65;

/** 送信側の帯域制限（ハイパス）で選べる範囲 (Hz)。`client/core` の `HIGHPASS_MIN_HZ` / `MAX` の写し。 */
export const HIGHPASS_MIN_HZ = 20;
export const HIGHPASS_MAX_HZ = 500;
/** 送信側の帯域制限（ローパス）の下限 (Hz)。上限は部屋ごとに [`autoLowpassHz`] で決まる。 */
export const LOWPASS_MIN_HZ = 1000;

/**
 * 部屋ごとのローパスの上限 (Hz)。**`client/core` の `auto_lowpass_hz` の写し**。
 *
 * コーデックの標本化周波数の半分（ナイキスト周波数）の 0.85 倍。これより上を通すと
 * 標本化周波数の変換で折り返し雑音が出るので、つまみの最大値にもこの値を使う。
 * Opus 8 kHz なら 3400 Hz、16 kHz なら 6800 Hz。
 */
export function autoLowpassHz(p: AudioProfile): number {
  return (p.sample_rate_hz / 2) * 0.85;
}

/** コーデックの表示名。 */
function codecName(codec: string): string {
  return codec === "opus" ? "Opus" : codec;
}

/** 設定を 1 行にする。例: `Opus 16 kHz / 20 ms ごと / 16 kbps / 可変・無送信・欠落対策 / ジッタバッファ 20 ms` */
export function describeProfile(p: AudioProfile): string {
  const parts = [
    `${codecName(p.codec)} ${p.sample_rate_hz / 1000} kHz`,
    `${packetMs(p)} ms ごと`,
    `${p.bitrate_bps / 1000} kbps`,
  ];
  const flags = [p.vbr && "可変", p.dtx && "無送信", p.fec && "欠落対策"].filter(Boolean);
  if (flags.length > 0) parts.push(flags.join("・"));
  parts.push(`ジッタバッファ ${jitterDepthMs(p)} ms`);
  return parts.join(" / ");
}

/**
 * 設定を「項目・値」の行に分ける（**画面で縦に並べる用**）。
 *
 * 1 行にまとめた [`describeProfile`] は選択肢（`<option>`）で使う。選択肢の中には
 * 要素を置けないので 1 行のままにし、画面に出す詳細はこちらを縦に並べる。
 */
export function profileRows(p: AudioProfile): [string, string][] {
  const flags = [p.vbr && "可変", p.dtx && "無送信", p.fec && "欠落対策"].filter(Boolean);
  return [
    ["コーデック", `${codecName(p.codec)} ${p.sample_rate_hz / 1000} kHz`],
    [
      "パケット間隔",
      `${packetMs(p)} ms`,
    ],
    ["ビットレート", `${p.bitrate_bps / 1000} kbps`],
    ["機能", flags.length > 0 ? flags.join("・") : "なし"],
    ["ジッタバッファ", `${jitterDepthMs(p)} ms`],
  ];
}

/**
 * 設定から導ける想定値を、同じ「項目・値」の形で返す。計算式は DOC-0021 §3。
 *
 * **どちらも「約」付きの計算値**で、実測は下の「状態・統計」に出る。
 * 帯域は**片方向・音声 1 本ぶん**なので、SFU で N 人の声が届く側は受信がこの N−1 倍になる。
 */
export function estimateRows(p: AudioProfile, denoise = false): [string, string][] {
  const note = denoise ? `・ノイズ抑制 +${denoiseDelayMs(p)} ms を含む` : "";
  return [
    ["回線上の帯域", `約 ${estimatedWireKbps(p).toFixed(1)} kbps（片方向・音声 1 本）`],
    ["片方向遅延", `約 ${estimatedOneWayMs(p, denoise)} ms（回線の遅れを除く${note}）`],
  ];
}

/** 合成音声（MCU がまとめた 1 本）の送信元 ID。`protocol` の `ParticipantId::MIXED`。 */
export const MIXED_PARTICIPANT_ID = 0;

/** 音量の調節つまみ 1 つぶん。 */
export interface GainTarget {
  /** SFU では**音声フレームの送信元**、MCU では**名簿の参加者**（`viaServer` が true のとき）。 */
  id: number;
  /**
   * 画面に出す名前。**掛ける場所（サーバの合成か端末の中か）は入れない。**
   * 場所は `viaServer` を見て、つまみの説明と保存ログの行末で言う（`main.ts`）。
   * 2026-10-08 まで名前側にも入れていて、ログが
   * 「#2 白川（サーバの合成で） を +16 dB にしました（サーバの合成で）」と二重になっていた（#67 ①）。
   */
  label: string;
  /**
   * **サーバに頼むつまみか**（2026-10-07 追加。#58）。
   *
   * `false`（既定）は端末の中で掛ける（即時・SFU の受信ゲインと MCU の合成音声ぜんぶ）。
   * `true` は `set_mix_gain` をサーバへ送る（**MCU で 1 人ずつ**。混ざった後から
   * 取り出せないので、サーバが混ぜるときに掛けるしかない）。反映にひと往復かかる。
   */
  viaServer?: boolean;
}

/**
 * **音量のつまみを出す相手を決める。**
 *
 * - **SFU**: 送信元ごとに音が届くので、自分以外の 1 人ずつに出す。端末の中で掛かる
 * - **MCU**: 合成音声 1 本しか届かないので、**2 種類**出す
 *   1. 合成音声ぜんぶ（端末の中。即時）
 *   2. **名簿の 1 人ずつ（サーバの合成で掛ける。#58）** — 名簿は方式に関係なく届いている
 *
 * 自分の声は自分には届かないので、どちらの方式でも自分には出さない。
 */
export function gainTargets(
  topology: Topology,
  roster: Participant[],
  self: number | null,
): GainTarget[] {
  const others = roster.filter((p) => p.id !== self);
  if (topology === "mcu") {
    return [
      { id: MIXED_PARTICIPANT_ID, label: "合成音声ぜんぶ" },
      ...others.map((p) => ({
        id: p.id,
        label: `#${p.id} ${p.name}`,
        viaServer: true,
      })),
    ];
  }
  return others.map((p) => ({ id: p.id, label: `#${p.id} ${p.name}` }));
}

/**
 * **退室を受けて畳む「音声フレームの送信元」を決める**（#56 ⑤）。
 *
 * `client/core` が抱えているのは**音声フレームの送信元**で、名簿の参加者とは
 * 一致しないことがある。方式で渡すものが変わる。
 *
 * - **SFU**: 送信元 = 参加者。名簿から消えた ID をそのまま返す
 * - **MCU**: 届くのは合成音声 1 本（[`MIXED_PARTICIPANT_ID`]）だけなので、参加者の
 *   ID を渡しても意味がない。サーバが合成を止めるのは**参加者が 2 人を割ったとき**
 *   なので、**自分以外が居なくなったときに限って**合成音声を返す
 *
 * `roster` は**退室を反映した後**のものを渡す。
 */
export function departedStreamIds(
  topology: Topology,
  roster: Participant[],
  self: number | null,
  left: number[],
): number[] {
  if (left.length === 0) return [];
  if (topology === "mcu") {
    const others = roster.filter((p) => p.id !== self);
    return others.length === 0 ? [MIXED_PARTICIPANT_ID] : [];
  }
  // 自分の送信元は手元に無い（自分の声は自分に届かない）ので、念のため外す
  return left.filter((id) => id !== self);
}

/** ゲインの表示（`+6 dB` / `0 dB` / `-6 dB`）。上げたのか下げたのかが一目で分かるように符号を付ける。 */
export function formatGainDb(db: number): string {
  return `${db > 0 ? "+" : ""}${db} dB`;
}

/** 部屋を選択肢 1 行にする。例: `Opus 音質重視（SFU・0/16 人） — Opus 16 kHz / …` */
export function describeRoom(room: RoomInfo): string {
  const topology = room.topology === "sfu" ? "SFU" : "MCU";
  return (
    `${room.display_name}（${topology}・${room.participants}/${room.max_participants} 人）` +
    ` — ${describeProfile(room.audio_profile)}`
  );
}

/**
 * 設定から見積もる回線上の量 (kbps)。固定ビットレート換算なので、可変では実測のほうが下がる。
 * DOC-0021 §3-1 と同じ式。
 */
export function estimatedWireKbps(p: AudioProfile): number {
  const payload = Math.round((p.bitrate_bps * packetMs(p)) / 8000);
  const packetsPerSecond = 1000 / packetMs(p);
  return ((payload + 12 + OVERHEAD_PER_PACKET) * 8 * packetsPerSecond) / 1000;
}

/**
 * **ノイズ抑制が足す片方向遅延 (ms)。**
 *
 * RNNoise は 48 kHz・480 サンプル (10 ms) の枠で動き、出力が 1 枠遅れる
 * (`client/core` の `dsp::denoise`)。**いまの 3 部屋 (10 / 20 / 40 ms) はどれも 480 の倍数
 * なので 10 ms**。480 で割り切れないフレーム長 (5 ms。いまの `rooms.conf` には無い) では、
 * 最初の端数ぶん無音を足すのでその分だけ増える。
 */
export function denoiseDelayMs(p: AudioProfile): number {
  return p.frame_ms < 10 ? 10 + p.frame_ms : 10;
}

/**
 * 設定から見積もる片方向遅延 (ms。回線の遅れを除く)。DOC-0021 §3-2 と同じ式。
 *
 * `denoise` が true ならノイズ抑制の 1 枠ぶん ([`denoiseDelayMs`]) を足す。
 */
export function estimatedOneWayMs(p: AudioProfile, denoise = false): number {
  // 符号化の先読み。標本化周波数によらず一定（実測。protocol の opus::LOOKAHEAD_MS）
  const lookahead = 6.5;
  // 48 kHz ⇄ コーデックの周波数の変換 (送受 2 段)。48 kHz なら 0
  const resample = { 8000: 4.6, 12000: 3.0, 16000: 2.3, 24000: 1.5, 48000: 0 }[p.sample_rate_hz] ?? 0;
  const ns = denoise ? denoiseDelayMs(p) : 0;
  return Math.round((packetMs(p) + lookahead + resample + jitterDepthMs(p) + ns) * 10) / 10;
}

/** core の stats_json（数えた総数）。 */
export interface EngineStats {
  sent: {
    frames: number;
    packets: number;
    bytes: number;
    speech: number;
    untransmitted: number;
    /** 無音が続いたので送らずに飛ばしたフレーム数（#64。古い WASM には無い）。 */
    skipped?: number;
  };
  received: {
    streams: number;
    packets: number;
    bytes: number;
    played: number;
    lost: number;
    recovered: number;
    late: number;
    resyncs: number;
    stalls: number;
    trimmed: number;
    restarts: number;
    malformed: number;
  };
}

/** 区間の集計（前回との差から出す）。 */
export interface Rates {
  /** 送信の回線上の量 (kbps。付き物を含む)。 */
  sentKbps: number;
  /**
   * **無音が続いたので送らずに飛ばしたフレーム数**（#64）。
   *
   * 送信側は無音のあいだ 200 ms ごとに 1 つしか送らない。ここが増えていて「送信」が
   * 下がっていれば間引きが効いている（10 ms の部屋なら 61.6 → 3.1 kbps）。
   */
  skippedSilence: number;
  /** 受信の回線上の量 (kbps)。 */
  receivedKbps: number;
  /** 1 パケットの平均バイト数（本実装のヘッダを含む）。 */
  sentBytesPerPacket: number;
  /** 欠落率 (0〜1)。届いた + 欠落を分母にする。 */
  lossRatio: number;
  /** この区間で欠落した数。 */
  lost: number;
  /**
   * この区間で**遅れて届いて間に合わなかった**数。
   *
   * 欠落率にはこれも含まれるので、**回線で落ちたのか、ジッタバッファが薄くて
   * 捨てたのか**を切り分けるために別に出す（2026-10-01 の試聴で必要になった）。
   */
  late: number;
  /**
   * この区間で**ジッタバッファを作り直した**数。
   *
   * 順序番号が 1 秒分以上飛んだとき（再接続・長い途切れ）に、溜めてあるものを捨てて
   * 新しい番号から溜め直す。**増えているなら回線が一度死んでいる**合図（`client/core`
   * の `jitter.rs`）。相手が黙って止まっただけでは増えない。
   */
  resyncs: number;
  /**
   * この区間の**ジッタバッファ待ち**（2026-10-06 追加）。
   *
   * ジッタバッファが空になって 1 パケットぶん待った数。欠落ではない（待ったパケットは
   * 次の取り出しで鳴る）ので欠落率には入れない。
   * **ここが増えているなら、回線で落ちているのではなくジッタバッファが浅い。** `jitter_ms` を
   * 増やすか、遅延変動の出どころ（主スレッド経由の受信など）を疑う。
   */
  stalls: number;
  /**
   * この区間の**再生停止**（2026-10-06 追加）。
   *
   * 0.5 秒ぶん連続で取り出せなかったときに、再生を止めてジッタバッファを作り直した数。
   * **1 回につき約 0.5 秒の無音**になるので、「音がごそっと飛ぶ」体感に直結する。
   * 0 でないなら、まずここを消しに行く。
   */
  restarts: number;
  /**
   * この区間で**送信できなかった Datagram** の数（2026-10-07 追加）。
   *
   * WebTransport の送信キューが埋まると黙って捨てられる。送信側は順序番号を進めるので、
   * **捨てられた分はサーバには「欠落」として見える**（回線に出ていないので NIC にも
   * UDP の計数器にも残らない）。**ここが 0 でなければ、上りの欠落はこれが原因**（タスク #54）。
   */
  sendFailures: number;
  /**
   * この区間の実秒数。
   *
   * **率を再現するのに必要。** 区間の長さが分からないと「1 秒あたり何回」に直せない
   * （2026-10-07 追加。保存ログが 5 秒ごとなのに中身は直前 1 秒ぶんだった問題の対処）。
   */
  seconds: number;
}

/** 総数の差から区間の値を出す。`seconds` が 0 以下なら 0 を返す。 */
const ZERO_RATES: Rates = {
  sentKbps: 0,
  skippedSilence: 0,
  receivedKbps: 0,
  sentBytesPerPacket: 0,
  lossRatio: 0,
  lost: 0,
  late: 0,
  resyncs: 0,
  stalls: 0,
  restarts: 0,
  sendFailures: 0,
  seconds: 0,
};

/**
 * 2 回の統計の差から「いまの速さ」を出す。
 *
 * **差は 0 で下限を切る。** 数はすべて累計で減らない作りだが（`client/core` の
 * `Receiver` は捨てた送信元ぶんを累計へ移す）、古い WASM と組み合わさった場合に
 * 減ることがあり、そのまま計算すると負の kbps が出てしまう（2026-10-01 に実測）。
 */
export function ratesBetween(
  prev: EngineStats,
  now: EngineStats,
  seconds: number,
  /** プラットフォーム層（メインスレッド）が数えている送信失敗の、この区間ぶんの差分。 */
  sendFailures = 0,
): Rates {
  if (seconds <= 0) return { ...ZERO_RATES };
  // 項目が無い古い WASM と組み合わさっても NaN を出さないよう、undefined は 0 として扱う
  const d = (a: number, b: number) => Math.max(0, (a || 0) - (b || 0));
  const sentPackets = d(now.sent.packets, prev.sent.packets);
  const skipped = d(now.sent.skipped ?? 0, prev.sent.skipped ?? 0);
  const sentBytes = d(now.sent.bytes, prev.sent.bytes);
  const recvPackets = d(now.received.packets, prev.received.packets);
  const recvBytes = d(now.received.bytes, prev.received.bytes);
  const lost = d(now.received.lost, prev.received.lost);
  const played = d(now.received.played, prev.received.played);
  const wire = (bytes: number, packets: number) =>
    ((bytes + packets * OVERHEAD_PER_PACKET) * 8) / seconds / 1000;
  return {
    sentKbps: wire(sentBytes, sentPackets),
    skippedSilence: skipped,
    receivedKbps: wire(recvBytes, recvPackets),
    sentBytesPerPacket: sentPackets > 0 ? sentBytes / sentPackets : 0,
    lossRatio: played + lost > 0 ? lost / (played + lost) : 0,
    lost,
    late: d(now.received.late, prev.received.late),
    resyncs: d(now.received.resyncs, prev.received.resyncs),
    stalls: d(now.received.stalls, prev.received.stalls),
    restarts: d(now.received.restarts, prev.received.restarts),
    sendFailures: Math.max(0, sendFailures),
    seconds,
  };
}

/** 画面に出す 1 行（実測）。 */
/**
 * 実測を「項目・値」の行に分ける（**画面で縦に並べる用**。部屋の設定と同じ形）。
 *
 * 欠落率には「遅れて届いて間に合わなかった」ぶんも入るので、区間ごとの内訳を添える
 * （ジッタバッファが薄いせいなのか、回線で落ちたのかを切り分けるため）。
 *
 * 「ジッタバッファ待ち」は欠落率に入らない別枠（2026-10-06 追加）。**ここと「再生停止」が
 * 増えているときはジッタバッファが浅い**ので、`jitter_ms` を上げるか遅延変動の出どころを探す。
 */
export function rateRows(r: Rates, stats: EngineStats): [string, string][] {
  const pct = (x: number) => `${(x * 100).toFixed(1)}%`;
  return [
    [
      "送信",
      `${r.sentKbps.toFixed(1)} kbps（1 パケット平均 ${r.sentBytesPerPacket.toFixed(1)} B` +
        ` / 無音で省いた枠 ${r.skippedSilence}）`,
    ],
    ["受信", `${r.receivedKbps.toFixed(1)} kbps`],
    [
      "欠落",
      `${pct(r.lossRatio)}（欠落 ${r.lost} / 遅れて到着 ${r.late} / ジッタバッファ再作成 ${r.resyncs}）`,
    ],
    [
      "ジッタバッファ待ち",
      `${r.stalls} 回（再生停止 ${r.restarts} 回 = 約 ${(r.restarts * 0.5).toFixed(1)} 秒の無音）`,
    ],
    ["送信できなかった", `${r.sendFailures} 個（WebTransport の送信キューが埋まった分）`],
    ["冗長コピーから復元", `${stats.received.recovered} 回（累計）`],
    ["送信元", String(stats.received.streams)],
  ];
}

/** 実測を 1 行にする。**保存するログ用**（1 行 1 時刻で並べたいので画面とは形を変える）。 */
export function formatRates(r: Rates, stats: EngineStats): string {
  // **区間の秒数を先頭に置く。** 回数を「1 秒あたり」に直すのに必要で、
  // これが無いと後から率を再現できない（2026-10-07 追加）
  return (
    `区間 ${r.seconds.toFixed(1)} 秒 / ` +
    rateRows(r, stats)
      .map(([k, v]) => `${k} ${v}`)
      .join(" / ")
  );
}

/** stats_json を読む。形が違えば例外。 */
export function parseStats(json: string): EngineStats {
  return JSON.parse(json) as EngineStats;
}
